/**
 * Portal page view (ADR-0010 P7b2 · B+C) — fetches a page (AST + RLS-resolved
 * queries) and renders it inside the branded `PortalShell` (header +
 * collapsible nav + footer). Restricted pages that 401/403 for an anonymous
 * visitor bounce to the portal login.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";

import { PageRenderer } from "@/components/portal/PageRenderer";
import { PageRuntimeProvider } from "@/components/portal/PageRuntime";
import { PortalShell } from "@/components/portal/PortalShell";

import { NotAuthorized } from "./NotAuthorized";
import { ApiError } from "@/lib/api";
import { portalApi } from "@/lib/api-portal";
import {
  parentLauncherPath,
  parentLoginPath,
  usePortalAuth,
} from "@/lib/portalAuth";

export function PortalPageView() {
  const { appSlug = "", pageSlug = "home", rowId } = useParams();
  const location = useLocation();
  const { user, logout } = usePortalAuth();

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ["portal-page", appSlug, pageSlug, user?.id ?? "anon"],
    queryFn: () => portalApi.getPage(appSlug, pageSlug),
    retry: false,
  });

  // A page is gated when the loader fails for an auth reason:
  //  - 401 → the session is missing/expired (clear the stale token below);
  //  - ANONYMOUS + any 4xx (403 / 404 / unknown-app) → bounce to the portal
  //    login. A visitor with no session must NOT see the app shell nor learn
  //    whether a page/app exists (existence-hiding) — they authenticate first.
  // A 403/404 while SIGNED IN is a real outcome (wrong role → NotAuthorized;
  // missing page → "Page not found"; unknown app → launcher), NOT a login
  // bounce (else the login→home→login loop).
  const status = error instanceof ApiError ? error.status : undefined;
  // The membership/role distinction rides in the error MESSAGE (the code is
  // the generic "authorization_error"): `portal:not_a_member` (whole app) vs
  // `portal:page_role_denied` (member, wrong role for THIS page).
  const message = error instanceof ApiError ? error.message : undefined;
  const needsLogin =
    status === 401 || (!user && (status === 403 || status === 404));

  // Signed in but NOT a member of this app — they reached an app they don't
  // belong to (deep link). Bounce to the portal launcher (which lists only
  // their apps) when the portal is on. Two signals: a membership 403, or a
  // page that loaded with a user but no role (a public page / the home).
  const notMember =
    !!user &&
    (message?.includes("portal:not_a_member") ||
      (!!data && !!data.user && !data.role));

  // The app slug itself doesn't resolve (deleted / renamed / stale bookmark).
  // The backend now returns 404 `portal:unknown_app` (was a 401, which logged
  // the user out and looped them through login). Send a signed-in user to their
  // launcher instead of stranding them on a dead "page not found".
  const appGone = status === 404 && !!message?.includes("portal:unknown_app");

  // Drop a stale/expired token so the login screen starts clean.
  useEffect(() => {
    if (status === 401 && user) logout();
  }, [status, user, logout]);

  if (needsLogin) {
    // The portal is the single login door (ADR-0016) → bounce to it with a
    // ?next back to this deep-linked page. Use the full pathname (+ any query) so
    // a deep-linked DETAIL (`/p/<page>/<rowId>`) returns to the SAME row after login.
    return <Navigate to={parentLoginPath(location.pathname + location.search)} replace />;
  }

  // Signed-in NON-member of this app (deep link to an app they don't belong
  // to), OR the app no longer exists → the launcher, which lists only their
  // apps. (An anonymous visitor on a gone app falls through to the message.)
  if (notMember || (appGone && user)) {
    return <Navigate to={parentLauncherPath()} replace />;
  }

  return (
    <PortalShell
      appSlug={appSlug}
      activePageSlug={pageSlug}
      user={user}
      onSignOut={() => logout()}
    >
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {/* A member who lacks the role for THIS page keeps the nav + a clean
          not-authorized empty-state (not a bare red line). */}
      {error && !needsLogin && status === 403 && <NotAuthorized />}
      {error && !needsLogin && status !== 403 && (
        <p className="text-sm text-danger">
          {status === 404 ? "Page not found." : "Failed to load the page."}
        </p>
      )}
      {data && (
        // KEY on the page slug + the row PATH segment + the URL query string so the
        // runtime REMOUNTS on every navigation — not just the first mount. Without
        // it, a revisit to a page whose query is already CACHED returns `data`
        // synchronously (no undefined gap), so React REUSES the provider instead of
        // remounting it: the reactive-query mount effect (fires once per mount) never
        // re-runs and `$page` isn't re-seeded, so a detail page opened a second time
        // (e.g. catalogo → detail → gestione → catalogo → detail) shows no data. The
        // row identity (path `/:rowId` OR a legacy `?row=` in the search) keys it, so
        // opening a DIFFERENT row remounts + re-seeds `$page.row`. (`$global` survives
        // — it lives at module scope keyed by app slug, not in this subtree.)
        <PageRuntimeProvider
          key={`${appSlug}/${pageSlug}/${rowId ?? ""}${location.search}`}
          appSlug={appSlug}
          result={data}
          globalVariables={data.globalVariables}
          // Readable detail URL: `/p/<page>/<rowId>` seeds `$page.row` from the PATH
          // (initialVars wins over the AST default; the legacy `?row=` query still
          // works via PageRuntime's generic query-param seeding).
          initialVars={rowId ? { row: rowId } : undefined}
          refresh={() => void refetch()}
        >
          <PageRenderer result={data} />
        </PageRuntimeProvider>
      )}
    </PortalShell>
  );
}
